import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "react-modal";
import { FaArrowLeft, FaSpinner } from "react-icons/fa";
import verifiedIcon from "../assets/Verified.svg";
import notVerifiedIcon from "../assets/Notverified.svg";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { validateToken } from "../tokenValidation";
import secureStorage from "../utils/secureStorage";

Modal.setAppElement("#root");

export default function Viewdoctor() {
  const API = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const spinner = <FaSpinner className="animate-spin inline-block mr-2" />;

  const [doctors, setDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);
  const [blobUrls, setBlobUrls] = useState({});

  const [addOpen, setAddOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sigOpen, setSigOpen] = useState(false);

  const [toChange, setToChange] = useState(null);
  const [forSig, setForSig] = useState(null);

  const [newDoc, setNewDoc] = useState({
    name: "",
    healthcareProfessionalId: "",
    mobileNumber: "",
    emailId: ""
  });
  const [sigFile, setSigFile] = useState(null);

  const fetchDoctors = async () => {
    setLoadingDoctors(true);
    try {
      const token = secureStorage.get("authToken");
      const orgId = secureStorage.get("orgId");
      if (!token) return;
      const res = await fetch(`${API}hospital/get-all-practitioners?orgId=${orgId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-HIP-ID": secureStorage.get("clinic")
        }
      });
      if (!res.ok) throw new Error();
      const { data } = await res.json();
      const list = data || [];
      setDoctors(list);
      await Promise.all(
        list.filter(d => d.signatureUrl).map(d => fetchSignature(d.id))
      );
    } catch {
      toast.error("Failed to load doctors");
    } finally {
      setLoadingDoctors(false);
    }
  };

  const fetchSignature = async id => {
    try {
      const token = secureStorage.get("authToken");
      const resp = await fetch(
        `${API}hospital/practitioner-signature?practitionerId=${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-HIP-ID": secureStorage.get("clinic")
          }
        }
      );
      if (!resp.ok) return;
      const blob = await resp.blob();
      setBlobUrls(prev => ({ ...prev, [id]: URL.createObjectURL(blob) }));
    } catch {}
  };

  const toggleStatus = async () => {
    setLoadingAction(true);
    const newStatus = toChange.active ? "INACTIVE" : "ACTIVE";
    try {
      const token = secureStorage.get("authToken");
      const res = await fetch(
        `${API}hospital/update-practitioner-status?practitionerId=${toChange.healthcareProfessionalId}&status=${newStatus}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-HIP-ID": secureStorage.get("clinic")
          }
        }
      );
      if (!res.ok) throw new Error();
      toast.success(`Status set to ${newStatus}`);
      fetchDoctors();
    } catch {
      toast.error("Status change failed");
    } finally {
      setLoadingAction(false);
      setConfirmOpen(false);
    }
  };

  const addDoctor = async () => {
    setLoadingAction(true);
    const { name, healthcareProfessionalId, mobileNumber, emailId } = newDoc;
    if (!name || !healthcareProfessionalId || !mobileNumber || !emailId) {
      toast.error("All fields required");
      setLoadingAction(false);
      return;
    }

    const selectedOrgId = secureStorage.get("orgId");
    const payload = {
      ...newDoc,
      orgId: selectedOrgId,
    };

    try {
      const token = secureStorage.get("authToken");
      const res = await fetch(`${API}hospital/add-practitioner`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-HIP-ID": secureStorage.get("clinic")
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error();
      toast.success("Doctor added");
      setAddOpen(false);
      setNewDoc({ name: "", healthcareProfessionalId: "", mobileNumber: "", emailId: "" });
      fetchDoctors();
    } catch {
      toast.error("Add failed");
    } finally {
      setLoadingAction(false);
    }
  };

  const uploadSignature = async () => {
    setLoadingAction(true);
    if (!sigFile || !forSig) {
      toast.error("Select file");
      setLoadingAction(false);
      return;
    }
    try {
      const token = secureStorage.get("authToken");
      const form = new FormData();
      form.append("practitionerId", forSig.id);
      form.append("file", sigFile);
      const res = await fetch(`${API}hospital/upload-practitioner-signature`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-HIP-ID": secureStorage.get("clinic")
        },
        body: form
      });
      if (!res.ok) throw new Error();
      toast.success("Signature uploaded");
      setSigOpen(false);
      fetchDoctors();
    } catch {
      toast.error("Upload failed");
    } finally {
      setLoadingAction(false);
    }
  };

  useEffect(() => {
    validateToken(navigate);
    fetchDoctors();
    return () => Object.values(blobUrls).forEach(URL.revokeObjectURL);
  }, []);

  return (
    <div className="bg-[#F6F6F6] min-h-screen p-6">
      <div className="flex items-center justify-between mb-6 relative">
        <button
          onClick={() => navigate(-1)}
          className="absolute left-0 flex items-center text-teal-700 hover:text-teal-900 font-medium"
        >
          <FaArrowLeft className="mr-2" /> Back
        </button>
        <h1 className="w-full text-center text-3xl font-bold text-gray-800">All Doctors</h1>
      </div>

      <div className="rounded-lg overflow-hidden shadow-md" style={{ backgroundColor: "#DEEBEC" }}>
        <div className="flex justify-between items-center p-4">
          <h2 className="text-lg font-bold text-[#0F766E]">Doctor Details</h2>
          <button
            onClick={() => setAddOpen(true)}
            className="px-8 py-3 bg-teal-600 text-white font-medium rounded-[10px] focus:ring-teal-500"
          >
            {loadingAction && addOpen ? spinner : "Add Doctor"}
          </button>
        </div>

        {loadingDoctors ? (
          <p className="text-center py-6">Loading...</p>
        ) : (
          <table className="w-full table-auto">
            <thead className="bg-[#DEEBEC] border-b border-[#0F766E]">
              <tr>
                <th className="px-4 py-3 text-left text-[15px] font-semibold text-[#0F766E]">Sr.</th>
                <th className="px-4 py-3 text-left text-[15px] font-semibold text-[#0F766E]">Name</th>
                <th className="px-4 py-3 text-left text-[15px] font-semibold text-[#0F766E]">HP ID</th>
                <th className="px-4 py-3 text-left text-[15px] font-semibold text-[#0F766E]">Mobile</th>
                <th className="px-4 py-3 text-left text-[15px] font-semibold text-[#0F766E]">Email</th>
                <th className="px-4 py-3 text-left text-[15px] font-semibold text-[#0F766E]">Signature</th>
                <th className="px-4 py-3 text-center text-[15px] font-semibold text-[#0F766E]">Status</th>
              </tr>
            </thead>
            <tbody>
              {doctors.map((doc, idx) => (
                <tr key={doc.id} className="bg-[#DEEBEC] border-b border-[#C6D9DB]">
                  <td className="px-4 py-3">{idx + 1}</td>
                  <td className="px-4 py-3">{doc.name}</td>
                  <td className="px-4 py-3">{doc.healthcareProfessionalId}</td>
                  <td className="px-4 py-3">{doc.mobileNumber}</td>
                  <td className="px-4 py-3">{doc.emailId}</td>
                  <td className="px-4 py-3 flex items-center space-x-2">
                    {blobUrls[doc.id] ? (
                      <img src={blobUrls[doc.id]} alt="Signature" className="w-16 h-8 object-contain border" />
                    ) : (
                      <span className="text-gray-500 text-sm">None</span>
                    )}
                    <button
                      onClick={() => { setForSig(doc); setSigOpen(true); }}
                      className="text-sm text-[#0F766E] hover:underline"
                    >
                      {doc.signatureUrl ? "Update" : "Upload"}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => { setToChange(doc); setConfirmOpen(true); }}
                      className={`w-6 h-6 mx-auto ${doc.active ? "bg-green-500" : "bg-red-500"} rounded-full`}
                    >
                      <img
                        src={doc.active ? verifiedIcon : notVerifiedIcon}
                        alt={doc.active ? "Verified" : "Not Verified"}
                        className="w-4 h-4 mx-auto"
                      />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        isOpen={addOpen}
        onRequestClose={() => setAddOpen(false)}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
      >
        <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
          <h2 className="text-2xl font-bold mb-4">Add Doctor</h2>
          <input
            type="text"
            placeholder="Name"
            value={newDoc.name}
            onChange={e => setNewDoc({ ...newDoc, name: e.target.value })}
            className="w-full border p-2 rounded mb-3"
          />
          <input
            type="text"
            placeholder="HP ID"
            value={newDoc.healthcareProfessionalId}
            onChange={e => setNewDoc({ ...newDoc, healthcareProfessionalId: e.target.value })}
            className="w-full border p-2 rounded mb-3"
          />
          <input
            type="text"
            placeholder="Mobile"
            value={newDoc.mobileNumber}
            onChange={e => setNewDoc({ ...newDoc, mobileNumber: e.target.value })}
            className="w-full border p-2 rounded mb-3"
          />
          <input
            type="email"
            placeholder="Email"
            value={newDoc.emailId}
            onChange={e => setNewDoc({ ...newDoc, emailId: e.target.value })}
            className="w-full border p-2 rounded mb-4"
          />
          <div className="flex justify-end space-x-4">
            <button onClick={() => setAddOpen(false)} className="px-4 py-2 bg-gray-300 rounded">Cancel</button>
            <button onClick={addDoctor} className="px-4 py-2 bg-teal-600 text-white rounded">
              {loadingAction ? spinner + "Adding..." : "Add"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={confirmOpen}
        onRequestClose={() => setConfirmOpen(false)}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
      >
        <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-sm">
          <h2 className="text-xl font-bold mb-2">Confirm Status Change</h2>
          <p className="mb-4">
            Change status of <strong>{toChange?.name}</strong> to <strong>{toChange?.active ? "INACTIVE" : "ACTIVE"}</strong>?
          </p>
          <div className="flex justify-end space-x-4">
            <button onClick={() => setConfirmOpen(false)} className="px-4 py-2 bg-gray-300 rounded">No</button>
            <button onClick={toggleStatus} className="px-4 py-2 bg-teal-600 text-white rounded">
              {loadingAction ? spinner + "Saving..." : "Yes"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={sigOpen}
        onRequestClose={() => setSigOpen(false)}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
      >
        <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-sm">
          <h2 className="text-xl font-bold mb-2">Upload Signature for {forSig?.name}</h2>
          <input type="file" accept="image/*" onChange={e => setSigFile(e.target.files[0])} className="mb-4" />
          <div className="flex justify-end space-x-4">
            <button onClick={() => setSigOpen(false)} className="px-4 py-2 bg-gray-300 rounded">Cancel</button>
            <button onClick={uploadSignature} className="px-4 py-2 bg-teal-600 text-white rounded">
              {loadingAction ? "Uploading..." : "Upload"}
            </button>
          </div>
        </div>
      </Modal>

      <ToastContainer />
    </div>
  );
}
